import { initializeApp, FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  ConfirmationResult,
  Auth
} from 'firebase/auth';
import { storage } from '../utils/storage';
import { authApi } from './api';

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;
let auth: Auth | null = null;
let recaptchaVerifier: RecaptchaVerifier | null = null;

// Store confirmationResult in a more persistent way
// Using a closure with a backup in window for HMR survival
let _confirmationResult: ConfirmationResult | null = null;

// Also store in window as backup
declare global {
  interface Window {
    __firebaseConfirmationResult?: ConfirmationResult | null;
  }
}

const getConfirmationResult = (): ConfirmationResult | null => {
  // Try local variable first, then window backup
  if (_confirmationResult) {
    console.log('Got confirmationResult from local variable');
    return _confirmationResult;
  }
  if (window.__firebaseConfirmationResult) {
    console.log('Got confirmationResult from window backup');
    return window.__firebaseConfirmationResult;
  }
  return null;
};

const setConfirmationResult = (result: ConfirmationResult | null): void => {
  console.log('Setting confirmationResult:', result ? 'has value' : 'null');
  _confirmationResult = result;
  window.__firebaseConfirmationResult = result;
};

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

export const initFirebase = (): FirebaseApp | null => {
  if (app) return app;

  // Check if Firebase config is available
  if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
    console.warn('Firebase configuration missing. Push notifications disabled.');
    return null;
  }

  try {
    app = initializeApp(firebaseConfig);
    console.log('Firebase initialized');
    return app;
  } catch (error) {
    console.error('Failed to initialize Firebase:', error);
    return null;
  }
};

export const initMessaging = async (): Promise<Messaging | null> => {
  if (messaging) return messaging;

  if (!app) {
    app = initFirebase();
  }

  if (!app) return null;

  try {
    messaging = getMessaging(app);
    return messaging;
  } catch (error) {
    console.error('Failed to initialize Firebase Messaging:', error);
    return null;
  }
};

export const requestNotificationPermission = async (): Promise<boolean> => {
  if (!('Notification' in window)) {
    console.warn('Notifications not supported in this browser');
    return false;
  }

  const permission = await Notification.requestPermission();
  return permission === 'granted';
};

export const getFCMToken = async (): Promise<string | null> => {
  const msg = await initMessaging();
  if (!msg || !VAPID_KEY) {
    console.warn('Cannot get FCM token: messaging not initialized or VAPID key missing');
    return null;
  }

  try {
    // Wait for the service worker to be ready
    const swRegistration = await navigator.serviceWorker.ready;
    console.log('Service Worker ready for FCM');

    const currentToken = await getToken(msg, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swRegistration,
    });

    if (currentToken) {
      const storedToken = storage.getFcmToken();

      // Only update if token is different
      if (currentToken !== storedToken) {
        storage.setFcmToken(currentToken);
        console.log('FCM token updated locally');

        // Update token on server if user is authenticated
        if (storage.getAccessToken()) {
          try {
            await authApi.updateFcmToken(currentToken);
            console.log('FCM token synced with server');
          } catch (error) {
            console.error('Failed to update FCM token on server:', error);
          }
        }
      }

      return currentToken;
    } else {
      console.warn('No FCM token available');
      return null;
    }
  } catch (error) {
    console.error('Error getting FCM token:', error);
    return null;
  }
};

export const onForegroundMessage = (callback: (payload: {
  notification?: { title?: string; body?: string };
  data?: Record<string, string>;
}) => void): (() => void) => {
  if (!messaging) {
    console.warn('Messaging not initialized');
    return () => {};
  }

  return onMessage(messaging, (payload) => {
    console.log('Foreground message received:', payload);
    callback(payload);
  });
};

export const setupNotifications = async (): Promise<boolean> => {
  console.log('Setting up notifications...');
  console.log('Current permission status:', Notification.permission);

  // Check if notifications are supported
  if (!('Notification' in window)) {
    console.warn('Notifications not supported in this browser');
    return false;
  }

  // Check if service workers are supported
  if (!('serviceWorker' in navigator)) {
    console.warn('Service workers not supported');
    return false;
  }

  const hasPermission = await requestNotificationPermission();
  console.log('Permission result:', hasPermission ? 'granted' : 'denied/dismissed');

  if (!hasPermission) {
    console.log('Notification permission not granted');
    return false;
  }

  const token = await getFCMToken();
  console.log('FCM token obtained:', token ? 'yes' : 'no');

  if (!token) {
    console.log('Could not get FCM token');
    return false;
  }

  console.log('Notifications setup complete!');
  return true;
};

// Check if notifications are enabled
export const areNotificationsEnabled = (): boolean => {
  if (!('Notification' in window)) return false;
  return Notification.permission === 'granted' && !!storage.getFcmToken();
};

// Get notification permission status
export const getNotificationPermissionStatus = (): NotificationPermission | 'unsupported' => {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
};

// Disable notifications (clear token from server)
export const disableNotifications = async (): Promise<boolean> => {
  try {
    // Clear token from server
    if (storage.getAccessToken()) {
      await authApi.clearFcmToken();
      console.log('FCM token cleared from server');
    }

    // Clear local token
    storage.setFcmToken('');
    console.log('FCM token cleared locally');

    return true;
  } catch (error) {
    console.error('Error disabling notifications:', error);
    return false;
  }
};

// ==========================================
// PHONE AUTH FUNCTIONS
// ==========================================

export const initAuth = (): Auth | null => {
  if (auth) return auth;

  if (!app) {
    app = initFirebase();
  }

  if (!app) return null;

  try {
    auth = getAuth(app);
    // Set language to French
    auth.languageCode = 'fr';
    return auth;
  } catch (error) {
    console.error('Failed to initialize Firebase Auth:', error);
    return null;
  }
};

export const setupRecaptcha = (containerId: string, forceNew: boolean = false): RecaptchaVerifier | null => {
  // If already set up and not forcing new, return existing
  if (recaptchaVerifier && !forceNew) {
    console.log('reCAPTCHA already initialized, reusing');
    return recaptchaVerifier;
  }

  const firebaseAuth = initAuth();
  if (!firebaseAuth) {
    console.error('Firebase Auth not initialized');
    return null;
  }

  const container = document.getElementById(containerId);
  if (!container) {
    console.error(`reCAPTCHA container not found: ${containerId}`);
    return null;
  }

  // Clear existing verifier if any
  if (recaptchaVerifier) {
    try {
      recaptchaVerifier.clear();
    } catch (e) {
      console.warn('Error clearing old reCAPTCHA:', e);
    }
    recaptchaVerifier = null;
  }

  // Clear any existing reCAPTCHA elements in the container
  container.innerHTML = '';

  try {
    recaptchaVerifier = new RecaptchaVerifier(firebaseAuth, containerId, {
      size: 'invisible',
      callback: () => {
        console.log('reCAPTCHA solved successfully');
      },
      'expired-callback': () => {
        console.log('reCAPTCHA expired - user needs to re-verify');
      },
      'error-callback': (error: Error) => {
        console.error('reCAPTCHA error:', error);
      }
    });

    console.log('reCAPTCHA verifier created');
    return recaptchaVerifier;
  } catch (error) {
    console.error('Failed to setup reCAPTCHA:', error);
    return null;
  }
};

export const sendVerificationCode = async (phoneNumber: string): Promise<boolean> => {
  const firebaseAuth = initAuth();
  if (!firebaseAuth) {
    console.error('Firebase Auth not initialized');
    throw new Error('Firebase Auth non initialisé');
  }

  if (!recaptchaVerifier) {
    console.error('reCAPTCHA not initialized');
    throw new Error('reCAPTCHA non initialisé. Rechargez la page.');
  }

  try {
    // Format phone number with country code if not present
    const formattedPhone = phoneNumber.startsWith('+')
      ? phoneNumber
      : `+33${phoneNumber.replace(/^0/, '')}`;

    console.log('Sending verification to:', formattedPhone);

    const result = await signInWithPhoneNumber(
      firebaseAuth,
      formattedPhone,
      recaptchaVerifier
    );
    setConfirmationResult(result);
    console.log('Verification code sent, confirmationResult stored');
    return true;
  } catch (error: any) {
    console.error('Failed to send verification code:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);

    // Reset reCAPTCHA on error
    if (recaptchaVerifier) {
      try {
        recaptchaVerifier.clear();
      } catch (e) {
        console.warn('Error clearing reCAPTCHA:', e);
      }
      recaptchaVerifier = null;
    }
    throw error;
  }
};

export const verifyCode = async (code: string): Promise<string> => {
  const confirmationResult = getConfirmationResult();
  if (!confirmationResult) {
    console.error('No confirmation result found in window');
    throw new Error('No confirmation result available');
  }

  try {
    const result = await confirmationResult.confirm(code);
    console.log('Phone verified successfully', result.user);
    setConfirmationResult(null);

    // Get the Firebase ID token to send to backend
    const idToken = await result.user.getIdToken();
    return idToken;
  } catch (error) {
    console.error('Failed to verify code:', error);
    throw error;
  }
};

export const clearRecaptcha = (): void => {
  if (recaptchaVerifier) {
    recaptchaVerifier.clear();
    recaptchaVerifier = null;
  }
  setConfirmationResult(null);
};

export default {
  init: initFirebase,
  initMessaging,
  requestPermission: requestNotificationPermission,
  getToken: getFCMToken,
  onForegroundMessage,
  setup: setupNotifications,
  areNotificationsEnabled,
  getNotificationPermissionStatus,
  disableNotifications,
  // Phone Auth
  initAuth,
  setupRecaptcha,
  sendVerificationCode,
  verifyCode,
  clearRecaptcha,
};
